import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { clsx } from "clsx";
import { CheckCircle2, ExternalLink, Loader2, Sparkles, XCircle } from "lucide-react";
import { customApi, type CustomListingDetail, type NicheIdeaView } from "../../lib/customListing";

// Custom Liste Studyosu — mikro nis ailesi. Bitmis listeden (sablon yolu) kardes taslaklar:
// alicinin yazdigi nis metni degisir (SENIOR MOM -> SENIOR DAD), ornekler/mockuplar/kolaj ayni
// sablondan yeni nisle basilir, baslik ve etiketler nise uyarlanir. Her nis ayri panel taslagi.

export function NicheFamily({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const saved = rec.template.niches?.items ?? [];
  const [items, setItems] = useState<NicheIdeaView[]>(saved);
  useEffect(() => { setItems(saved); }, [JSON.stringify(saved.map((n) => [n.id, n.status, n.draftId]))]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [count, setCount] = useState(12);
  const [err, setErr] = useState<string | null>(null);
  const templated = rec.template.pkg?.status === "ready" && (rec.template.samples?.rows?.length ?? 0) > 0 && rec.template.samples?.mode !== "fast";
  const suggest = useMutation({
    mutationFn: () => customApi<{ items: NicheIdeaView[] }>("POST", `/${rec.id}/niches/suggest`, { count }),
    onSuccess: (r) => { setErr(null); setItems(r.items); setSel(new Set(r.items.filter((n) => !n.draftId).map((n) => n.id))); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const build = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/niches/build`, { items: items.filter((n) => sel.has(n.id)) }),
    onSuccess: () => { setErr(null); setSel(new Set()); onChanged(); },
    onError: (e) => setErr((e as Error).message),
  });
  const upd = (id: string, patch: Partial<NicheIdeaView>) => setItems((l) => l.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  const running = items.some((n) => n.status === "running");
  const inputCls = "rounded-lg border border-gray-200 px-2 py-1 text-xs focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200";

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="mb-1 text-sm font-semibold text-gray-900">Niş ailesi (kardeş listeler)</h3>
      <p className="mb-3 text-xs text-gray-500">Aynı tasarım başka mikro nişlerde: alıcının yazdığı niş metni değişir (Senior Mom → Senior Dad, Cheer Mom → Baseball Mom). Örnekler, mockuplar ve kolaj aynı şablondan yeni nişle basılır; başlık ve etiketler satan listenin yapısı korunarak uyarlanır. Her niş ayrı bir panel taslağı olur, sipariş şablonu ortaktır.</p>
      {!templated ? (
        <p className="text-sm text-gray-600">Niş ailesi şablon yolu ister: 3. adımda şablonu kur ve 4. adımda örnekleri şablondan bas. (AI yolundaki listeler için sonra eklenecek.)</p>
      ) : !rec.links.draftId ? (
        <p className="text-sm text-gray-600">Önce ana listenin taslağını kur ("Taslağa çevir"); kardeş listeler onun galerisini ve şablonunu kullanır.</p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <label htmlFor="niche-count" className="text-xs text-gray-600">Öneri sayısı</label>
            <input id="niche-count" type="number" min={3} max={30} value={count} onChange={(e) => setCount(Math.max(3, Math.min(30, Number(e.target.value) || 12)))} className={`${inputCls} w-16`} />
            <button onClick={() => suggest.mutate()} disabled={suggest.isPending || running}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {suggest.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-indigo-600" />} Niş önerileri üret
            </button>
            {items.some((n) => !n.draftId) && (
              <button onClick={() => build.mutate()} disabled={build.isPending || running || !sel.size}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                {build.isPending || running ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {running ? "Taslaklar kuruluyor…" : `Seçilenlerin taslaklarını kur (${sel.size})`}
              </button>
            )}
          </div>
          {err && <p className="mb-2 text-sm text-red-600">{err}</p>}
          {items.length > 0 && (
            <div className="space-y-2">
              {items.map((n) => (
                <div key={n.id} className={clsx("rounded-lg border p-2", n.draftId ? "border-emerald-200 bg-emerald-50/40" : "border-gray-200")}>
                  <div className="flex flex-wrap items-center gap-2">
                    {!n.draftId && <input type="checkbox" aria-label={`${n.niche} seç`} checked={sel.has(n.id)} disabled={running} onChange={() => setSel((s) => { const x = new Set(s); if (x.has(n.id)) x.delete(n.id); else x.add(n.id); return x; })} className="rounded border-gray-300 text-indigo-600" />}
                    <span className="text-sm font-semibold text-gray-900">{n.niche}</span>
                    {Object.entries(n.values).map(([k, v]) => (
                      <input key={k} aria-label={`${n.niche} ${k}`} value={v} disabled={!!n.draftId || running} onChange={(e) => upd(n.id, { values: { ...n.values, [k]: e.target.value } })} className={`${inputCls} w-40 font-medium`} />
                    ))}
                    {n.status === "running" && <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Kuruluyor…</span>}
                    {n.status === "failed" && <span className="inline-flex items-center gap-1 text-xs text-red-600"><XCircle className="h-3.5 w-3.5" /> {n.error}</span>}
                    {n.draftId && (
                      <Link to={`/listing-builder?id=${n.draftId}`} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"><CheckCircle2 className="h-3.5 w-3.5" /> Taslak hazır <ExternalLink className="h-3 w-3" /></Link>
                    )}
                  </div>
                  <input aria-label={`${n.niche} başlık`} value={n.title} maxLength={140} disabled={!!n.draftId || running} onChange={(e) => upd(n.id, { title: e.target.value })} className={`${inputCls} mt-1.5 w-full`} />
                  <p className="mt-1 text-[11px] text-gray-500">{n.tags.length}/13 etiket: {n.tags.join(", ")}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
